import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  Venue,
  Event,
  VenueContactEntry,
  CreateVenueInput,
  UpdateVenueInput,
  AddVenueContactInput,
  UpdateVenueContactInput,
} from '@peles/shared';
import * as venuesApi from '../api/venues';

export function useVenues(filters?: { isActive?: boolean; city?: string }) {
  return useQuery<Venue[]>({
    queryKey: ['venues', filters],
    queryFn: () => venuesApi.listVenues(filters),
  });
}

export function useVenue(id: string) {
  return useQuery<Venue>({
    queryKey: ['venues', id],
    queryFn: () => venuesApi.getVenue(id),
    enabled: !!id,
  });
}

export function useCreateVenue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateVenueInput) => venuesApi.createVenue(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venues'] });
    },
  });
}

export function useUpdateVenue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateVenueInput }) =>
      venuesApi.updateVenue(id, updates),
    onSuccess: (updatedVenue) => {
      queryClient.setQueryData(['venues', updatedVenue.id], updatedVenue);
      queryClient.invalidateQueries({ queryKey: ['venues'] });
    },
  });
}

export function useDeactivateVenue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => venuesApi.deactivateVenue(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venues'] });
    },
  });
}

export function useVenueEvents(venueId: string) {
  return useQuery<Event[]>({
    queryKey: ['venues', venueId, 'events'],
    queryFn: () => venuesApi.listVenueEvents(venueId),
    enabled: !!venueId,
  });
}

// ─── Venue Contacts ─────────────────────────────────────────────────────────

export function useVenueContacts(venueId: string) {
  return useQuery<VenueContactEntry[]>({
    queryKey: ['venues', venueId, 'contacts'],
    queryFn: () => venuesApi.listVenueContacts(venueId),
    enabled: !!venueId,
  });
}

export function useAddVenueContact(venueId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddVenueContactInput) => venuesApi.addVenueContact(venueId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venues', venueId, 'contacts'] });
    },
  });
}

export function useUpdateVenueContact(venueId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contactId, updates }: { contactId: string; updates: UpdateVenueContactInput }) =>
      venuesApi.updateVenueContact(venueId, contactId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venues', venueId, 'contacts'] });
    },
  });
}

export function useRemoveVenueContact(venueId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (contactId: string) => venuesApi.removeVenueContact(venueId, contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venues', venueId, 'contacts'] });
    },
  });
}
